import * as React from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  makeStyles,
  mergeClasses,
} from '@fluentui/react-components';

export type FullReviewAction = 'confirm' | 'cancel';

interface FullReviewPromptProps {
  open: boolean;
  onResolve: (action: FullReviewAction) => void;
}

const SIGNUS_BLUE = '#1014f5';
const SIGNUS_BLUE_HOVER = '#2a2eff';
const SIGNUS_BLUE_PRESSED = '#0a0dcc';

const useStyles = makeStyles({
  content: {
    fontSize: '14px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  button: {
    minWidth: 'auto',
    paddingLeft: '16px',
    paddingRight: '16px',
    borderRadius: '8px',
    whiteSpace: 'nowrap',
    '& *': { whiteSpace: 'nowrap' },
  },
  secondaryText: {
    color: SIGNUS_BLUE,
    ':hover': { color: SIGNUS_BLUE_HOVER },
    ':hover:active': { color: SIGNUS_BLUE_PRESSED },
  },
  primary: {
    backgroundColor: SIGNUS_BLUE,
    border: `1px solid ${SIGNUS_BLUE}`,
    color: '#ffffff',
    ':hover': {
      backgroundColor: SIGNUS_BLUE_HOVER,
      border: `1px solid ${SIGNUS_BLUE_HOVER}`,
      color: '#ffffff',
    },
    ':hover:active': {
      backgroundColor: SIGNUS_BLUE_PRESSED,
      border: `1px solid ${SIGNUS_BLUE_PRESSED}`,
      color: '#ffffff',
    },
  },
});

const FullReviewPrompt: React.FC<FullReviewPromptProps> = ({ open, onResolve }) => {
  const styles = useStyles();
  return (
    <Dialog
      open={open}
      onOpenChange={(_, data) => {
        if (!data.open) onResolve('cancel');
      }}
    >
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Run full document review</DialogTitle>
          <DialogContent className={styles.content}>
            <span>
              Sigi will scan the entire document and propose ~10–25 targeted edits — additions,
              deletions, and tightenings — applied as Word redlines.
            </span>
            <span>
              Track Changes will be enabled if it isn’t already so each edit shows as a tracked
              change you can accept or reject in Word’s Review panel.
            </span>
          </DialogContent>
          <DialogActions>
            <Button
              className={mergeClasses(styles.button, styles.secondaryText)}
              onClick={() => onResolve('cancel')}
            >
              Cancel
            </Button>
            <Button
              className={mergeClasses(styles.button, styles.primary)}
              appearance="primary"
              onClick={() => onResolve('confirm')}
            >
              Start review
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default FullReviewPrompt;
